import { ApolloQueryResult } from 'apollo-client-ppc';
import type { PartnerOrderEstimate } from '@css-xp/ppc-types';
import type { Currency, Promotion } from '@css-xp/graphtypes/src/generated-types-new';
import { PromotionInstance } from '@atlassian/commerce-ccp-gateway-client';
import { CCPPricing } from '@atlassian/cssxp-typescommon';
import { FetchEntitlementsForCartQueryType } from '@css-xp/ppc-cart-common';
import { FetchNativeProductCollectionForChangeSubscriptionQuery } from '@css-xp/graphtypes/src/generated-types-sxp';
import {
  ProductStatus,
  CloudSiteTypeEnum,
  OperationType,
  LinkedEntitlementRelationShipType,
  NativeProductResponse,
} from '@css-xp/ppc-purchase-experience';
import { AppPromotionResponse } from './store/types';

export type ContainerId = string | null | undefined;

export type ContainerOrProductId = string;

export type CartProductContact = { name: string; email?: string; accountId?: string; picture?: string | null };

export enum PromotionTypes {
  PARTNER_DISCOUNT = 'PARTNER_DISCOUNT',
  DISCRETIONARY_DISCOUNT = 'DISCRETIONARY_DISCOUNT',
}

export enum PromotionApplicationReason {
  CLOUD_MIGRATION_CREDIT = 'cloud.migration.credit',
}

export interface CartProductSchema {
  id: string;
  itemId?: string; // existing `cartProduct` will have an `itemId` generated during transformation of entitlement into store state. NEW `cartProduct` will have an `id` which is a `uuid` generated during creating the `cartProduct` instance, which is used to track it's selection in the cart. As this `id` for new products is a uuid, the same is sent as `itemId` for new products.
  name: string;
  entitlementNumber: string;
  productId: string;
  pricingPlanId: string;
  customerContacts: CartProductContact[];
  total?: string | number;
  listPrice?: string | number;
  totalTax?: string | number;
  partnerMargin?: string | number;
  otherDiscount?: string | number;
  nextBillDate: string;
  nextBillAnchorDate: string;
  activeUsers: string;
  billingCycle: string;
  cloudSiteId: string;
  cloudSite: string;
  cloudSiteType: CloudSiteTypeEnum;
  offeringId: string;
  offeringName?: string;
  offeringLevel?: number;
  containerId?: ContainerId;
  productStatuses?: ProductStatus[];
  // type of the product if its existing account product or transfer product
  operationType: OperationType;
  // In case of transfer below fields to be set
  sourceTransactionAccountId?: string;
  sourceInvoiceGroupId?: string;
  trialLengthInDays?: string;
  logoId?: string;
  inTrial?: boolean; // FIXME: refactor test data in `utils.spec.ts` and make this mandatory
  promotions: Promotion[];
  removePromotions?: PromotionInstance[];
  isExternalProduct?: boolean;
  isPartnerEntitlement?: boolean;
  hasAllData?: boolean;
  billingAdmins?: CartProductContact[];
  organizationId?: string | null;
  organisationName?: string | null;
  // parent storing the relation between sandbox and product
  sandboxParent?: string;
  // currently storing the collection parent entitlement id for the product
  parentEntitlementId?: string;
  isSandbox?: boolean;
  isOrgProduct?: boolean;
  linkedSandboxIds?: string[];
  pricingPlanType?: string;
  priceEligibility?: string;
  isFutureDatedSubscription?: boolean;
  inactiveReason?: string;
  isOnUpgradeTrial?: boolean;
}

export type CartLinkedEntitlementsSchema = {
  id: string;
  name: string;
  logoId: string;
  itemId?: string; // existing `app` will have an `itemId` generated during transformation of entitlement into store state. NEW `app` will have an `id` which is a `uuid` generated during creating the `app` instance, which is used to track it's selection in the cart. As this `id` for new apps is a uuid, the same is sent as `itemId` for new apps.
  entitlementNumber?: string;
  containerOrProductId: ContainerOrProductId;
  customerContacts?: CartProductContact[];
  activeUsers: string;
  billingCycle?: string;
  total?: number;
  listPrice?: number;
  totalTax?: number;
  partnerMargin?: number;
  otherDiscount?: number;
  nextBillDate?: string;
  cloudSiteId?: string;
  nextBillAnchorDate?: string;
  cloudSite?: string;
  cloudSiteType?: CloudSiteTypeEnum;
  productId: string;
  pricingPlanId: string;
  offeringId: string;
  offeringName: string;
  offeringLevel?: number;
  operationType: OperationType;
  trialLengthInDays?: string;
  promoCode?: string;
  parentEntitlementId?: string;
  nextBillingTimestamp?: string;
  productStatuses?: ProductStatus[];
  promotions?: AppPromotionResponse[];
  sourceInvoiceGroupId?: string;
  removePromotions?: PromotionInstance[];
  inTrial?: boolean;
  isPartnerEntitlement?: boolean;
  hasAllData?: boolean;
  billingAdmins?: CartProductContact[];
  // parent storing the relation between sandbox and product
  sandboxParent?: string;
  isSandbox?: boolean;
  linkedSandboxIds?: string[];
  pricingPlanType?: string;
  priceEligibility?: string;
  isFutureDatedSubscription?: boolean;
  inactiveReason?: string;
  isOnUpgradeTrial?: boolean;
  relationshipType?: LinkedEntitlementRelationShipType;
  cancelReason?: string;
};

export type PartialCartLinkedEntitlementsSchema = Partial<CartLinkedEntitlementsSchema>;

export interface ValidationErrorSchema {
  entitlementId: string;
  errors: {
    errorCode: string;
    message: string;
    field: string;
  }[];
}

export type BillEstimateResponse = {
  estimates: Record<
    string,
    {
      grandTotal: number;
      listPrice: number;
      totalDiscount: number;
      totalPartnerDiscount: number;
      totalOtherDiscount: number;
      discountPercent: number;
      total: number;
      totalTax: number;
      estimateIncludingPartnerDiscount: number;
      allPromotionsApplied: Array<any>;
      isAdminFeeApplicable: boolean;
      adminFee: number;
      itemLevelTaxInfo: Array<
        [
          string,
          {
            tax: number;
            taxPercent: number;
          }
        ]
      >;
    }
  > | null;
  taxItemsMap: Record<string, { tax: number; taxPercent: number }> | null;
  adminTaxDetailsMap: Record<string, { adminTaxItemLabel: string; totalAdminTax: number }> | null;
  partnerPricing: PartnerOrderEstimate.OverallPricing | null;
};

export type CollectionToInstanceOfferingMapResponse = ApolloQueryResult<FetchNativeProductCollectionForChangeSubscriptionQuery>;

export type ProductSubscriptionsWrapper = FetchEntitlementsForCartQueryType['transactionAccount']['productSubscriptions'];

type ProductSubscriptions = NonNullable<NonNullable<ProductSubscriptionsWrapper>['nodes']>;

export type ProductSubscription = NonNullable<ProductSubscriptions[number]>;

export type BillToInfo = {
  city: string;
  country: string;
  postalCode: string;
  state: string;
  street: string;
};

export interface Dictionary<T> {
  [Key: string]: T;
}

export interface BillingProfileResponse {
  id: string;
  name: string;
  currency: Currency;
  transactionAccount: {
    id: string;
  };
  active: boolean;
  shipToParty: {
    id: string;
    name: string;
    postalAddress: {
      line1: string;
      line2?: string;
      city: string;
      country: string;
      state?: string;
      phone?: string;
      postcode: string;
    };
    taxId?: string;
    taxIds?: string[];
    priceEligibility?: string;
  };
  defaultPaymentMethod: string;
  shipToPriceEligibility: CCPPricing;
}

export { ProductStatus, CloudSiteTypeEnum, OperationType, LinkedEntitlementRelationShipType };

export type { NativeProductResponse };
